import React, { useState, useEffect, useCallback } from "react";
import { Tabs } from "antd";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Clients from "./clients";
import LoginHistory from "./loginHistory";
import ClientForm from "./ClientForm";
import AccessControlRoutes from "./AccessControlRoutes";

const allTabs = [
  { key: "clients", label: "Clients List", path: "/settings/clients" },
  { key: "add-client", label: "Add New Client", path: "/settings/add-client" },
  { key: "edit-client", label: "Edit Client", path: "/settings/edit-client" },
  {
    key: "login-history",
    label: "Login History",
    path: "/settings/login-history",
  },
  {
    key: "access-control",
    label: "Access Control",
    path: "/settings/access-control",
  },
];

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const authState = useSelector((state) => state.auth);
  const profile = useSelector((state) => state.profile);
  console.log("Profile from Settings Tab:", profile);
  const [activeKey, setActiveKey] = useState("clients");
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientsRefreshKey, setClientsRefreshKey] = useState(0);

  const normalize = (str) => (str || "").replace(/\/+$/, "").toLowerCase();

  const getActiveKeyFromPath = useCallback(() => {
    const path = normalize(location.pathname);

    if (path.includes("/settings/clients")) return "clients";
    if (path.includes("/settings/add-client")) return "add-client";
    if (path.includes("/settings/edit-client")) return "edit-client";
    if (path.includes("/settings/login-history")) return "login-history";
    if (path.includes("/settings/access-control")) return "access-control";
    return "clients";
  }, [location.pathname]);

  const getIdFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("clientId");
  }, [location.search]);

  useEffect(() => {
    const active = getActiveKeyFromPath();
    setActiveKey(active);

    // Handle client ID from URL params
    const clientId = getIdFromQuery();
    if (clientId && active === "edit-client") {
      setEditingClientId(clientId);
    } else if (active === "add-client") {
      setEditingClientId(null);
    }
  }, [location.pathname, getActiveKeyFromPath, getIdFromQuery]);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        if (authState && profile) {
          setIsAdmin(
            profile.role === "admin" || profile.role === "super_admin"
          );
        }
      } catch (error) {
        console.error("Error checking user role:", error);
      }
    };

    checkUserRole();
  }, [authState, profile]);

  const onChange = (key) => {
    if (key === "edit-client" && editingClientId) {
      navigate(`/settings/edit-client?clientId=${editingClientId}`);
    } else {
      navigate(`/settings/${key}`);
    }
    setActiveKey(key);
  };

  const handleAddNewClient = () => {
    setEditingClientId(null);
    navigate("/settings/add-client");
    setActiveKey("add-client");
  };

  const handleEditClient = (clientId) => {
    setEditingClientId(clientId);
    // Navigate to edit client URL - the useEffect will automatically set the active key
    navigate(`/settings/edit-client?clientId=${clientId}`);
  };

  const handleClientSaved = (clientData) => {
    // Trigger refresh of clients list
    setClientsRefreshKey(prev => prev + 1);
    
    if (clientData && clientData.id && !editingClientId) {
      // If this was a new client creation, redirect to edit mode
      setEditingClientId(clientData.id);
      navigate(`/settings/edit-client?clientId=${clientData.id}`);
    } else {
      // If this was an update, go back to clients list
      navigate("/settings/clients");
    }
  };

  const handleClientDeleted = () => {
    navigate("/settings/clients");
  };

  const handleCancel = () => {
    navigate("/settings/clients");
  };

  const renderTabContent = () => {
    const clientId = getIdFromQuery();

    switch (activeKey) {
      case "clients":
        return (
          <Clients
            key={clientsRefreshKey}
            onAddNewClient={handleAddNewClient}
            onEditClient={handleEditClient}
          />
        );
      case "add-client":
        return (
          <ClientForm
            clientId={null}
            onClientSaved={handleClientSaved}
            onClientDeleted={handleClientDeleted}
            onCancel={handleCancel}
          />
        );
      case "edit-client":
        return (
          <ClientForm
            clientId={clientId}
            onClientSaved={handleClientSaved}
            onClientDeleted={handleClientDeleted}
            onCancel={handleCancel}
          />
        );
      case "login-history":
        return <LoginHistory />;
      case "access-control":
        return <AccessControlRoutes />;
      default:
        return <Navigate to="/settings/clients" replace />;
    }
  };

  if (!profile) return null;

  const urlClientId = getIdFromQuery();
  const availableTabs = allTabs.filter((tab) => {
    if (tab.key === "login-history" && !isAdmin) return false;
    if (tab.key === "access-control" && !isAdmin) return false;
    // Consider both state and URL when deciding to show the edit tab
    if (tab.key === "edit-client" && !(editingClientId || urlClientId))
      return false;
    return true;
  });

  return (
    <div className="page-wrapper">
      <div className="settings-content">
        <div className="settings-dashboard-tabs">
          <Tabs
            activeKey={activeKey}
            onChange={onChange}
            type="card"
            size="small"
            tabBarStyle={{ marginBottom: "5px" }}
            items={availableTabs.map((tab) => ({
              key: tab.key,
              label: tab.label,
              closable: false,
            }))}
          />
          <div className="tab-content">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
